import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ComponentType<any>;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  trend?: { value: number; direction: 'up' | 'down' | 'neutral' };
  loading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'blue',
  trend,
  loading = false,
}) => {
  const colorConfig = {
    blue: {
      bg: 'from-blue-50 to-blue-100/50',
      icon: 'from-blue-500 to-blue-600',
      text: 'text-blue-600',
      border: 'border-blue-200/50',
    },
    green: {
      bg: 'from-emerald-50 to-emerald-100/50',
      icon: 'from-emerald-500 to-emerald-600',
      text: 'text-emerald-600',
      border: 'border-emerald-200/50',
    },
    yellow: {
      bg: 'from-amber-50 to-amber-100/50',
      icon: 'from-amber-500 to-amber-600',
      text: 'text-amber-600',
      border: 'border-amber-200/50',
    },
    red: {
      bg: 'from-red-50 to-red-100/50',
      icon: 'from-red-500 to-red-600',
      text: 'text-red-600',
      border: 'border-red-200/50',
    },
    purple: {
      bg: 'from-purple-50 to-purple-100/50',
      icon: 'from-purple-500 to-purple-600',
      text: 'text-purple-600',
      border: 'border-purple-200/50',
    },
  };

  const config = colorConfig[color];

  return (
    <motion.div
      className="metric-card relative overflow-hidden group cursor-pointer"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      {/* Background gradient overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${config.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
      />

      <div className="relative z-10">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-r ${config.icon} flex items-center justify-center shadow-sm`}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-400">
                  {title}
                </h3>
                {subtitle && (
                  <p className="text-xs text-slate-500 dark:text-slate-500">{subtitle}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {loading ? (
                <div className="space-y-2">
                  <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              ) : (
                <>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-3xl font-bold text-slate-900 dark:text-white">
                      {value}
                    </span>
                    {trend && (
                      <div
                        className={`flex items-center text-sm font-medium ${
                          trend.direction === 'up'
                            ? 'text-emerald-600'
                            : trend.direction === 'down'
                              ? 'text-red-600'
                              : 'text-slate-500'
                        }`}
                      >
                        <TrendingUp
                          className={`w-4 h-4 mr-1 ${
                            trend.direction === 'down' ? 'rotate-180' : ''
                          }`}
                        />
                        {Math.abs(trend.value)}%
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hover effect border */}
      <div
        className={`absolute inset-0 border-2 ${config.border} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl`}
      />
    </motion.div>
  );
};

export default MetricCard;
